<template>
    <v-col>
        <RichDataTable :rich-data-table-controller="richDataTableController" dense>

            <template v-for="(header, index) in customFieldHeaders"
                      v-slot:[header.headerName]="{ item }">
                <span :key="index" class="caption">
                    {{ item.fields ? formatByHeader(item.fields[header.value], header) : '' }}
                </span>
            </template>

            <template v-slot:header.is_extra>
                <icon icon="BookmarkPlus"/>
            </template>
            <template v-slot:header.is_important>
                <icon icon="Flag"/>
            </template>

            <template v-slot:item.client="{item, index}">
                <Client :item="item"/>
            </template>
            <template v-slot:item.timeFeedback="{ item, index }">
                <TimeFeedback
                    :item="item"
                    :next-item="richDataTableController.getRows()[index + 1]"
                    :show-time-feedback="showTimeFeedback"
                    :show-time-feedback-options="showTimeFeedbackOptions" :show-new-day-feedback="showNewDayFeedback"
                    @updateTasks="$emit('updateTasks')"/>
            </template>
            <template v-slot:item.topic_id="{ item }">
                <Topic :name="getTopicName(item.topic_id)"/>
            </template>
            <template v-slot:item.time="{ item }">
                <Duration :time="item.time" :show-empty="false"/>
            </template>
            <template v-slot:item.person_id="{ item }">
                <Assignees :assignees="item.assignees"/>
            </template>
            <template v-slot:item.assignees="{ item }">
                <Assignees :assignees="item.mappedAssignees"/>
            </template>
            <template v-slot:item.is_important="{ item }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <div v-on="on">
                            <Icon v-if="item.is_important" icon="Flag"/>
                        </div>
                    </template>
                    {{ $t('Important') }}
                </v-tooltip>
            </template>
            <template v-slot:item.is_extra="{ item }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <div v-on="on">
                            <Icon v-if="item.is_extra" icon="BookmarkPlus"/>
                        </div>
                    </template>
                    {{ $t('Extra work') }}
                </v-tooltip>
            </template>
            <template v-slot:item.title="{ item }">
                <div class="d-flex align-center">
                    <OverflowTooltipText :title="item.title"/>
                    <div class="ml-1 d-flex align-center cursor-default">
                        <icon
                            v-if="item.has_logs"
                            icon="CommentFilled"
                            size="16"
                        />
                    </div>
                </div>
            </template>
            <template v-slot:item.start="{ item }">
                <Date :date="item.start"/>
            </template>
            <template v-slot:item.time_period="{ item }">
                <TimePeriod :start="item.start" :end="item.end"/>
            </template>
            <template v-slot:item.billable="{ item }">
                <Duration :show-empty="false" :time="item.billable"/>
            </template>
            <template v-slot:item.estimation="{ item }">
                <Duration :show-empty="false" :time="item.estimation"/>
            </template>
            <template v-slot:item.estimation_h="{ item }">
                <TimeHour :time="item.estimation"/>
            </template>
            <template v-slot:item.non_billable="{ item }">
                <Duration :show-empty="false" :time="item.non_billable"/>
            </template>
            <template v-slot:item.office="{ item }">
                {{ item.office ? $t('Yes') : '' }}
            </template>
            <template v-slot:item.status="{ item }">
                <TaskStatus :item="item"/>
            </template>
            <template v-slot:item.date="{ item }">
                <Time :time="item.date"/>
            </template>
            <template v-slot:item.due_date="{ item }">
                <Time :time="item.due_date"/>
            </template>
            <template v-slot:item.time_h="{ item }">
                <TimeHour :time="item.time"/>
            </template>
            <template v-slot:item.billable_h="{ item }">
                <TimeHour :time="item.billable"/>
            </template>
            <template v-slot:item.non_billable_h="{ item }">
                <TimeHour :time="item.non_billable"/>
            </template>

            <template v-slot:item.bill_by="{ item }">
                <BillBy :bill-by="item.bill_by"/>
            </template>
            <template v-slot:item.bill_at="{ item }">
                <Date :date="item.bill_at"/>
            </template>

            <template v-slot:item.time_entry_description="{ item }">
                <div class="d-flex align-center">
                    <icon-button :icon="item.description ? 'CommentFilled' : 'MessageSquare'"
                                 small
                                 button-small
                                 @click.stop="editCommentTimeEntry = item"/>
                    <OverflowTooltipText pointer :title="item.description" @click="onEditTimeEntryComment($event, item)"/>
                </div>
            </template>
            <template v-slot:item.client_ext_client_id="{ item }">
                <div class="caption"> {{ item.client.ext_client_id }}</div>
            </template>
            <template v-slot:item.client_default_person="{ item }">
                <Assignees :assignees="[item.client.default_person]"/>
            </template>
            <template v-slot:item.project_id="{ item }">
                <Project :project="item.project" dense/>
            </template>
        </RichDataTable>

        <EditReportTimeEntryDescription v-if="editCommentTimeEntry"
                                        :time-entry="editCommentTimeEntry"
                                        @close="editCommentTimeEntry = null"/>
    </v-col>
</template>

<script>
import RichDataTable from "@/components/RichDataTable/RichDataTable";
import TimeFeedback from "@/components/RichDataTable/components/TimeFeedback";
import Topic from "@/components/RichDataTable/components/Topic";
import Project from "@/components/RichDataTable/components/Project";
import Client from "@/components/RichDataTable/components/Client"
import Time from "@/components/RichDataTable/components/Time";
import Assignees from "@/components/RichDataTable/components/Assignees";
import OverflowTooltipText from "@/components/RichDataTable/components/OverflowTooltipText";
import Date from "@/components/RichDataTable/components/Date"
import BillBy from "@/components/RichDataTable/components/BillBy"
import TimePeriod from "@/components/RichDataTable/components/TimePeriod";
import Duration from "@/components/RichDataTable/components/Duration";
import TaskStatus from "@/components/RichDataTable/components/TaskStatus";
import TimeHour from "@/components/RichDataTable/components/TimeHour";
import DropMenu from "@/components/ModernFilterBar/components/DropMenu";
import EditReportTimeEntryDescription from "@/views/Report/EditReportTimeEntryDescription";

import {$t} from "../../i18n";
import RichDataTableController from "@/components/RichDataTable/RichDataTableController";
import {computed, ref} from "@vue/composition-api";

import {
    formatByHeader,
} from '../../plugins/formatting';
import {useTopicStore} from "../../store/modules/topic";

export default {
    name: "ReportTaskTime",
    components: {
        Client,
        RichDataTable,
        TimeFeedback,
        Topic,
        Project,
        Time,
        Assignees,
        OverflowTooltipText,
        Date,
        TimePeriod,
        Duration,
        TaskStatus,
        TimeHour,
        DropMenu,
        EditReportTimeEntryDescription,
        BillBy,
    },
    props: {
        richDataTableController: {
            type: RichDataTableController,
            required: true
        },
        showTimeFeedback: {
            type: Boolean,
            required: true
        },
        showTimeFeedbackOptions: {
            type: Boolean,
            required: true
        },
        showNewDayFeedback: {
            type: Boolean,
            required: true
        }
    },
    setup(props) {
        const editCommentTimeEntry = ref(null);
        const topicStore = useTopicStore()

        const customFieldHeaders = computed(() => {
            const customHeaders = props.richDataTableController.getVisibleHeaders().filter(
                x => x.group === 'custom'
                    || x.type === 'article'
            )

            return customHeaders.map(x => {
                x.headerName = 'item.' + x.value
                return x
            })
        })

        const onEditTimeEntryComment = (e, item) => {
            e.stopPropagation()
            editCommentTimeEntry.value = item
        }

        const getTopicName = (topicId) => {
            if (!topicId) {
                return null
            }

            const topic = topicStore.getTopicById(topicId)
            return topic ? topic.name : null
        }

        return {
            customFieldHeaders,
            editCommentTimeEntry,
            onEditTimeEntryComment,
            formatByHeader,
            topicStore,
            getTopicName
        }
    }
};
</script>
